import { useCallback, useRef } from 'react';
import { i18n } from '@/shared/i18n';
import { ENV } from '@/lib/config/env';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { getWorkspaceId } from '@/entities/workspace';
import { useFocusGroupRunStore } from '../stores/focusGroupRunStore';

interface RunFocusGroupParams {
  title: string;
  topic: string;
  personaIds: string[];
  transformedQuestions: string[];
  brandName?: string;
  analysisId?: string;
}

export const useRunFocusGroup = () => {
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(async (params: RunFocusGroupParams) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const baseUrl = ENV.INFERENCE_ENGINE_API_BASE_URL.trim().replace(/\/+$/, '');
    const url = `${baseUrl}/api/v1/focus-groups`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const workspaceId = getWorkspaceId();
    if (workspaceId) {
      headers['x-selected-workspace-id'] = workspaceId;
    }

    if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION && ENV.LOCAL_TOKEN) {
      headers['Authorization'] = ENV.LOCAL_TOKEN;
    }

    const response = await fetchWithAuth(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        title: params.title,
        topic: params.topic,
        personaIds: params.personaIds,
        transformedQuestions: params.transformedQuestions,
        brandName: params.brandName,
        analysisId: params.analysisId,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      useFocusGroupRunStore.getState().setError(
        i18n.t('audiences:focusGroup.useRunFocusGroup.requestFailedWithStatus', {
          status: response.status,
        }),
      );
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      useFocusGroupRunStore
        .getState()
        .setError(i18n.t('audiences:focusGroup.useRunFocusGroup.noResponseStreamAvailable'));
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop()!;

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6)) as Record<string, unknown>;
              handleEvent(eventType, data);
            } catch {
              // skip malformed JSON
            }
            eventType = '';
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        useFocusGroupRunStore
          .getState()
          .setError(
            err instanceof Error
              ? err.message
              : i18n.t('audiences:focusGroup.useRunFocusGroup.streamConnectionLost'),
          );
      }
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return { run, cancel };
};

function handleEvent(type: string, data: Record<string, unknown>) {
  const state = useFocusGroupRunStore.getState();

  switch (type) {
    case 'status':
      if (typeof data.focusGroupId === 'string') {
        state.setFocusGroupId(data.focusGroupId);
      }
      break;

    case 'persona_start':
      if (typeof data.personaId === 'string') {
        state.startPersona(
          data.personaId,
          typeof data.personaName === 'string'
            ? data.personaName
            : i18n.t('audiences:focusGroup.useRunFocusGroup.participant'),
          typeof data.totalPersonas === 'number' ? data.totalPersonas : 0,
        );
      }
      break;

    case 'question_ask':
      if (typeof data.personaId === 'string') {
        state.updatePersonaQuestion(
          data.personaId,
          data.questionIndex as number,
          data.totalQuestions as number,
        );
      }
      break;

    case 'persona_complete':
      if (typeof data.personaId === 'string') {
        state.completePersona(data.personaId);
      }
      break;

    case 'summary':
      state.setReportStatus('running');
      break;

    case 'persona_summaries':
      break;

    case 'complete':
      state.setComplete(typeof data.focusGroupId === 'string' ? data.focusGroupId : '');
      break;

    case 'error':
      state.setError(
        typeof data.message === 'string'
          ? data.message
          : i18n.t('audiences:focusGroup.useRunFocusGroup.focusGroupExecutionFailed'),
      );
      break;
  }
}
